import { useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { setBenefitsCaptureSettings, type StaffOrg } from '../services/staff'
import { DEFAULT_FRONT_DESK_GOAL, isValidFrontDeskGoal } from '../../shared/frontDeskGoal'

/** The slice of an org row this section edits. */
export interface BenefitsCaptureOrgSettings {
  benefitsCaptureEnabled: boolean
  frontDeskGoal: number | null
}

interface BenefitsCaptureSettingsProps {
  /** Org list, already loaded by StaffConsole (may be null while loading). */
  orgs: StaffOrg[] | null
  loadError: string | null
  /** The row's new values after a save, so the console's list stays true. */
  onSaved: (orgId: string, next: BenefitsCaptureOrgSettings) => void
}

const RENDER_CAP = 50

/**
 * One practice: the page switch and the goal, side by side. The checkbox
 * saves on toggle; the goal saves on its own button (or resets to the
 * default), and every save is one call to the setter carrying both values.
 */
function PracticeRow({ org, onSaved }: {
  org: StaffOrg
  onSaved: BenefitsCaptureSettingsProps['onSaved']
}) {
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState(String(org.frontDeskGoal ?? DEFAULT_FRONT_DESK_GOAL))

  const draftNumber = draft.trim() === '' ? NaN : Number(draft)
  const draftValid = isValidFrontDeskGoal(draftNumber)
  const shown = org.frontDeskGoal ?? DEFAULT_FRONT_DESK_GOAL
  const changed = draftValid && draftNumber !== shown

  async function save(enabled: boolean, goal: number | null, done: string) {
    setSaving(true)
    try {
      const res = await setBenefitsCaptureSettings(org.orgId, enabled, goal)
      onSaved(org.orgId, { benefitsCaptureEnabled: res.enabled, frontDeskGoal: res.goal })
      setDraft(String(res.goal ?? DEFAULT_FRONT_DESK_GOAL))
      toast.success(done)
    } catch (err) {
      Sentry.captureException(err)
      toast.error(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <li className="staff-org-row">
      <div className="staff-org-meta">
        <span className="staff-org-name">{org.name}</span>
        <span className="staff-org-id">{org.orgId}</span>
      </div>
      <div className="staff-org-actions">
        <label className={`sms-toggle${org.benefitsCaptureEnabled ? ' sms-toggle--on' : ''}`}>
          <input
            type="checkbox"
            checked={org.benefitsCaptureEnabled}
            disabled={saving}
            onChange={() => save(
              !org.benefitsCaptureEnabled,
              org.frontDeskGoal,
              org.benefitsCaptureEnabled ? `${org.name}: page off` : `${org.name}: page on`,
            )}
          />
          <span>Page on</span>
        </label>
        <span className="sms-mgmt-routes__label">Front-desk goal:</span>
        <input
          className="sms-mgmt-row__input"
          style={{ width: '4.5rem' }}
          type="number"
          min={1}
          max={100}
          step={1}
          value={draft}
          disabled={saving}
          aria-label={`Front-desk goal for ${org.name}`}
          onChange={(e) => setDraft(e.target.value)}
        />
        <span className="sms-mgmt-routes__label">
          % · {org.frontDeskGoal === null ? `default (${DEFAULT_FRONT_DESK_GOAL}%)` : 'set for this practice'}
        </span>
        <button
          className="staff-view-button"
          disabled={saving || !changed}
          title={draftValid ? undefined : 'A whole number from 1 to 100.'}
          onClick={() => save(org.benefitsCaptureEnabled, draftNumber, `${org.name}: goal ${draftNumber}%`)}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {org.frontDeskGoal !== null && (
          <button
            className="staff-view-button"
            disabled={saving}
            onClick={() => save(org.benefitsCaptureEnabled, null, `${org.name}: goal reset to default`)}
          >
            Reset to default
          </button>
        )}
      </div>
    </li>
  )
}

/**
 * Benefits Capture — the staff section that switches the page on per practice
 * and sets each practice's front-desk goal (story 4). Every practice starts
 * with the page off; checking the box makes it appear for every role of that
 * practice without a deploy, unchecking hides it again. Part of go-live-day
 * onboarding.
 */
export default function BenefitsCaptureSettings({ orgs, loadError, onSaved }: BenefitsCaptureSettingsProps) {
  const [filter, setFilter] = useState('')

  const filtered = useMemo(() => {
    if (!orgs) return []
    const q = filter.trim().toLowerCase()
    const list = q
      ? orgs.filter((o) => o.name.toLowerCase().includes(q) || o.orgId.toLowerCase().includes(q))
      : orgs
    return list.slice(0, RENDER_CAP)
  }, [orgs, filter])

  return (
    <div className="staff-emulator">
      <header className="staff-emulator__head">
        <div>
          <h1>Benefits Capture</h1>
          <p className="staff-console-subtitle">
            Switch the Benefits Capture page on per practice and set its front-desk goal.
            Every practice starts off; the switch takes effect for every role without a deploy.
          </p>
        </div>
      </header>
      <div className="staff-console-toolbar">
        <input
          type="search"
          placeholder="Search practices by name or ID…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Search practices"
        />
      </div>
      {loadError && <p className="auth-error">{loadError}</p>}
      {!orgs && !loadError && <p className="staff-console-loading">Loading organizations…</p>}
      {orgs && (
        <ul className="staff-org-list">
          {filtered.map((org) => (
            <PracticeRow key={org.orgId} org={org} onSaved={onSaved} />
          ))}
          {filtered.length === 0 && <li className="staff-org-empty">No practices match “{filter}”.</li>}
        </ul>
      )}
    </div>
  )
}
